// Known company codes for the Spam Rules Company filter/field — mirrors the
// Company/Channel/Account model in server/src/companies.js's COMPANIES map.
import { resolveAgentNamePlaceholders } from '@cxhub/shared'

export const KNOWN_COMPANIES = ['1A', 'AM', 'TRQ']

// Companies with a live customer-facing channel in CXHub today — TRQ has no
// channel built yet (its conversations don't exist), so it's hidden from the
// conversation list's Company toggle row until that changes. Deliberately
// scoped to that one control, not KNOWN_COMPANIES itself — admin/config
// contexts elsewhere (Flows catalog, Templates picker, Spam Rules) still need
// the full roster.
export const ACTIVE_CHANNEL_COMPANIES = KNOWN_COMPANIES.filter((c) => c !== 'TRQ')

// Shared company tag colors — used wherever a company needs to be
// unmistakable at a glance (the Agent page's conversation-header chip, the
// admin Flows page's row tag). Deliberately NOT the app's semantic MUI colors
// (primary/secondary/etc.), since 'primary' blue is already the dominant
// accent color everywhere else (nav, buttons, links) and would make the tag
// disappear into the surrounding chrome.
export const COMPANY_TAG_STYLE = {
  '1A': { bgcolor: '#e65100', color: '#fff' },   // strong orange
  'AM': { bgcolor: '#2e7d32', color: '#fff' },   // strong green
  'TRQ': { bgcolor: '#6a1b9a', color: '#fff' },  // strong purple
}

// ACCOUNT code -> bare COMPANY code — mirrors server/src/companies.js's
// ACCOUNTS registry, so client-side signature resolution accepts either an
// ACCOUNT code (conversation.company/SuggestionPanel's templateAccount prop,
// e.g. '1A_EBAY') or a bare COMPANY code, the same dual-key lookup
// companyInfo() does server-side.
const ACCOUNT_TO_COMPANY = {
  '1A_EBAY': '1A',
  'AM_EBAY': 'AM',
}

// Per-company outbound signature — mirrors server/src/companies.js's
// COMPANIES registry (signature/signatureIncludesAgentName fields only; the
// rest of that registry is server-only). Used by SuggestionPanel.jsx's
// draft-preview signature resolution so the client-side preview matches the
// rule the server enforces at send time (AM's signature never carries an
// agent's name — see companies.js's resolveSignature()). Checked before
// adding this: no endpoint exposes it to a plain csr-role agent today
// (admin-companies.js's GET / is admin-only and doesn't carry it
// anyway) — kept in sync here by hand, same as
// KNOWN_COMPANIES/COMPANY_TAG_STYLE above.
export const COMPANY_SIGNATURE = {
  '1A': { signature: '1A Auto', signatureIncludesAgentName: true },
  'AM': { signature: 'AM-Autoparts', signatureIncludesAgentName: false },
  'TRQ': { signature: 'TRQ Auto Parts', signatureIncludesAgentName: true },
}

// Per-company internal cc address for the return-label email's "send a copy
// to our own service@ mailbox" checkbox — mirrors channels/m365/config.js's
// server-side SEND_IDENTITIES/m365SendIdentityForCompany() (address field
// only), same "kept in sync here by hand" convention as COMPANY_SIGNATURE
// above. Shared by SuggestionPanel.jsx's send_return_label_email field and
// ManualActionsSidebar.jsx's resend_return_label_trq field, so the two
// default addresses can never drift apart. Fails open to 1A's address for an
// unknown/missing code, same as companySignatureInfo() — only ever a
// client-side DEFAULT; action-executor.js resolves its own real send
// identity independently at send time regardless of what this returns.
export function m365ServiceAddressForCompany(company) {
  return company === 'AM' ? 'service@am-autoparts.com' : 'service@1aauto.com'
}

// Resolve an ACCOUNT code ('1A_EBAY') or bare COMPANY code ('1A') to its
// signature entry. Unlike companies.js's companyInfo() (fail-closed, throws),
// this fails OPEN to 1A's signature for an unknown/missing code — it only
// affects a client-side draft preview, never what actually gets sent
// (companies.js's own fail-closed resolveSignature() is what runs at send
// time regardless of what this returns).
export function companySignatureInfo(code) {
  const companyCode = ACCOUNT_TO_COMPANY[code] ?? code
  return COMPANY_SIGNATURE[companyCode] ?? COMPANY_SIGNATURE['1A']
}

// The unresolved "[AGENT_FIRST_NAME] | <signature>" footer literal for a
// given account/company code — mirrors companies.js's server-side
// agentFooter(), for building/comparing against a not-yet-resolved draft
// footer client-side (SuggestionPanel.jsx's closed-reply starting text and
// template-insert paths).
export function clientAgentFooter(code) {
  return `[AGENT_FIRST_NAME] | ${companySignatureInfo(code).signature}`
}

// Resolves the "[AGENT_FIRST_NAME] | <signature>" placeholder embedded in
// `text` (anywhere, usually its final line) for a given account/company code
// and (possibly empty/null) agent first name. Mirrors companies.js's
// server-side resolveSignature() exactly: signatureIncludesAgentName: false
// (AM) always collapses to the bare company signature regardless of
// firstName; every other company pairs the name when one is available, or
// falls back to its own bare signature when it isn't (never a dangling
// leading " | "). Extracted here (rather than left as SuggestionPanel.jsx's
// own inline logic) so this — the actual bug-fix logic — is unit-testable
// without any React/JSX machinery, same convention as templateMerge.js/
// suggestionActionInputs.js.
//
// 2026-09-29: the rule itself is the shared resolveAgentNamePlaceholders()
// (shared/src/text-format.js), the same function behind the server's send
// path, so what a box shows after resolution is exactly what is sent. That
// also resolves a stray [AGENT_FIRST_NAME] outside the footer the way the
// server does (the name, or nothing) instead of leaving it literal.
export function resolvePlaceholderSignature(text, account, firstName) {
  return resolveAgentNamePlaceholders(text, companySignatureInfo(account), firstName)
}

// The Queue's Support toggle (2026-10-07), beside the 1A / AM buttons.
COMPANY_TAG_STYLE.Support = { bgcolor: '#1565c0', color: '#fff' } // strong blue
